import type { Rng } from './rng';
import type { BossKind } from './cues';
import type { MusicTheme } from './music';

export interface AmbienceParams {
  bed: 'wind' | 'rain' | 'dusk';
  lowHz: number;
  highHz: number;
  lfoHz: number;
  footstepsHz: number;
  machineryHz?: number;
}

export function ambienceParams(theme: Pick<MusicTheme, 'bpm' | 'droneHz'>): AmbienceParams {
  const footstepsHz = (theme.bpm / 60) * 2;
  if (theme.droneHz < 39) {
    return { bed: 'dusk', lowHz: 200, highHz: 400, lfoHz: 0.06, footstepsHz };
  }
  if (theme.droneHz < 50) {
    return { bed: 'rain', lowHz: 250, highHz: 7000, lfoHz: 0, footstepsHz, machineryHz: 55 };
  }
  return { bed: 'wind', lowHz: 300, highHz: 600, lfoHz: 0.1, footstepsHz };
}

export function footstepsGainForPhase(phase: string): number {
  return phase === 'march' || phase === 'engage' || phase === 'run' ? 0.035 : 0;
}

const buffers = new WeakMap<BaseAudioContext, AudioBuffer>();

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buffer = buffers.get(ctx);
  if (!buffer) {
    buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 2), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let state = 0x174c31;
    for (let i = 0; i < data.length; i++) {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      data[i] = (state / 0xffffffff) * 2 - 1;
    }
    buffers.set(ctx, buffer);
  }
  return buffer;
}

export interface AmbienceVoices {
  update(phase: string): void;
  stop(): void;
  restart(): void;
}

/** Persistent bed and aggregate feet are deliberately outside synth.VoicePool. */
export function buildAmbience(
  ctx: BaseAudioContext,
  dest: AudioNode,
  params: AmbienceParams,
  rng: Rng,
  bossKind: BossKind = 'brute',
): AmbienceVoices {
  let stopSources = (): void => {};
  let running = false;
  let phase = 'won';
  let nextStep = ctx.currentTime;
  let nextDrop = ctx.currentTime + 1;
  let nextWindShift = ctx.currentTime + 1;
  let tick = (): void => {};
  let stepGain: GainNode | null = null;
  let bossGain: GainNode | null = null;
  let bossDepth: GainNode | null = null;

  function startSources(): void {
    if (running) return;
    running = true;
    const sources: AudioScheduledSourceNode[] = [];
    const nodes: AudioNode[] = [];
    let windFilter: BiquadFilterNode | null = null;
    const bed = ctx.createBufferSource();
    bed.buffer = noiseBuffer(ctx);
    bed.loop = true;
    const bedGain = ctx.createGain();
    bedGain.gain.value = params.bed === 'rain' ? 0.018 : 0.035;
    nodes.push(bed, bedGain);
    bed.connect(bedGain);
    if (params.bed === 'rain') {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = params.highHz;
      bedGain.connect(filter);
      filter.connect(dest);
      nodes.push(filter);
    } else {
      const filter = ctx.createBiquadFilter();
      windFilter = filter;
      filter.type = 'bandpass';
      filter.frequency.value = (params.lowHz + params.highHz) / 2;
      filter.Q.value = 1.5;
      bedGain.connect(filter);
      filter.connect(dest);
      nodes.push(filter);
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = params.lfoHz;
      depth.gain.value = 0.018;
      lfo.connect(depth);
      depth.connect(bedGain.gain);
      lfo.start();
      sources.push(lfo);
      nodes.push(depth);
    }
    bed.start();
    sources.push(bed);

    if (params.bed === 'rain') {
      const drift = ctx.createOscillator();
      const depth = ctx.createGain();
      drift.frequency.value = 0.025;
      depth.gain.value = 0.25;
      drift.connect(depth);
      for (const frequency of [params.machineryHz! - 0.7, params.machineryHz! + 0.7]) {
        const hum = ctx.createOscillator();
        const gain = ctx.createGain();
        hum.type = 'sine';
        hum.frequency.value = frequency;
        depth.connect(hum.frequency);
        gain.gain.value = 0.006;
        hum.connect(gain);
        gain.connect(dest);
        hum.start();
        sources.push(hum);
        nodes.push(gain);
      }
      drift.start();
      sources.push(drift);
      nodes.push(depth);
    }

    if (bossKind !== 'brute') {
      const motor = ctx.createOscillator();
      const presence = ctx.createGain();
      const pulse = ctx.createOscillator();
      const depth = ctx.createGain();
      motor.type = bossKind === 'heli' ? 'triangle' : 'sawtooth';
      motor.frequency.value = bossKind === 'heli' ? 92 : 58;
      pulse.frequency.value = bossKind === 'heli' ? 8 : 3.5;
      depth.gain.value = phase === 'boss' ? (bossKind === 'heli' ? 0.008 : 0.004) : 0;
      bossDepth = depth;
      presence.gain.value = phase === 'boss' ? 0.012 : 0;
      pulse.connect(depth);
      depth.connect(presence.gain);
      motor.connect(presence);
      presence.connect(dest);
      motor.start();
      pulse.start();
      sources.push(motor, pulse);
      nodes.push(presence, depth);
      bossGain = presence;
    }

    const feet = ctx.createBufferSource();
    feet.buffer = noiseBuffer(ctx);
    feet.loop = true;
    const low = ctx.createBiquadFilter();
    low.type = 'bandpass';
    low.frequency.value = 260 + rng() * 80;
    low.Q.value = 0.9;
    const high = ctx.createBiquadFilter();
    high.type = 'bandpass';
    high.frequency.value = 850 + rng() * 300;
    high.Q.value = 0.7;
    const lowMix = ctx.createGain();
    lowMix.gain.value = 0.7;
    const highMix = ctx.createGain();
    highMix.gain.value = 0.3;
    const pulse = ctx.createGain();
    stepGain = pulse;
    pulse.gain.value = 0;
    feet.connect(low);
    feet.connect(high);
    low.connect(lowMix);
    high.connect(highMix);
    lowMix.connect(pulse);
    highMix.connect(pulse);
    pulse.connect(dest);
    feet.start();
    sources.push(feet);
    nodes.push(low, high, lowMix, highMix, pulse);

    let dropAt = nextDrop;
    const droplet = (): void => {
      if (footstepsGainForPhase(phase) === 0 || params.bed !== 'rain') return;
      const when = Math.max(ctx.currentTime + 0.01, dropAt);
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer(ctx);
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 3500 + rng() * 1800;
      const gain = ctx.createGain();
      const peak = 0.018 + rng() * 0.012;
      gain.gain.setValueAtTime(0.0001, when);
      gain.gain.exponentialRampToValueAtTime(peak, when + 0.003);
      gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.045);
      src.connect(filter);
      filter.connect(gain);
      gain.connect(dest);
      src.start(when, rng() * 1.9);
      src.stop(when + 0.05);
      src.onended = () => {
        src.disconnect();
        filter.disconnect();
        gain.disconnect();
      };
      dropAt = when + 1.2 + rng() * 2.8;
      nextDrop = dropAt;
    };

    stopSources = () => {
      running = false;
      for (const source of sources) {
        try {
          source.stop();
        } catch {
          // A source may already have ended during interruption.
        }
      }
      for (const node of nodes) node.disconnect();
    };

    tick = (): void => {
      if (!running) return;
      const now = ctx.currentTime;
      if (windFilter && now >= nextWindShift) {
        windFilter.frequency.linearRampToValueAtTime(params.lowHz + rng() * (params.highHz - params.lowHz), now + 1.5);
        nextWindShift = now + 2 + rng() * 3;
      }
      const footstepGain = footstepsGainForPhase(phase);
      if (footstepGain > 0) {
        while (nextStep < now + 0.08) {
          const when = Math.max(now + 0.01, nextStep);
          const velocity = footstepGain * (0.85 + rng() * 0.3);
          pulse.gain.setValueAtTime(0.0001, when);
          pulse.gain.linearRampToValueAtTime(velocity, when + 0.012);
          pulse.gain.setValueAtTime(velocity, when + 0.035);
          pulse.gain.linearRampToValueAtTime(0.0001, when + 0.07);
          nextStep += (1 / params.footstepsHz) * (0.98 + rng() * 0.04);
        }
        if (params.bed === 'rain' && (phase === 'march' || phase === 'engage' || phase === 'run') && now >= dropAt) droplet();
      }
    };
    tick();
  }

  startSources();
  return {
    update(nextPhase) {
      const wasWalking = footstepsGainForPhase(phase) > 0;
      phase = nextPhase;
      if (!running) return;
      if (bossGain) {
        bossGain.gain.setTargetAtTime(phase === 'boss' ? 0.012 : 0, ctx.currentTime, 0.15);
        bossDepth?.gain.setTargetAtTime(phase === 'boss' ? (bossKind === 'heli' ? 0.008 : 0.004) : 0, ctx.currentTime, 0.15);
      }
      if (wasWalking && footstepsGainForPhase(phase) === 0 && stepGain) {
        stepGain.gain.cancelScheduledValues(ctx.currentTime);
        stepGain.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
      }
      nextStep = Math.max(nextStep, ctx.currentTime + 0.01);
      tick();
    },
    stop() {
      if (running) stopSources();
    },
    restart() {
      startSources();
    },
  };
}
